// MediaPlayer.jsx
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.min.css';
import './MediaPlayer.scss';
import PdfViewer from '../PdfViewer/PdfViewer';
import AlternativePdfViewer from '../AlternativePdfViewer/AlternativePdfViewer';
import FullScreenImage from './FullScreenImage';
import { getMediaUrl } from '../../../helpers/utilities';
import { resolvePlaybackSource } from '../../../helpers/mediaPlayback';
import * as Sentry from '@sentry/react';

const getMediaType = (url) => {
    // Check if it's a blob URL (local file)
    if (url.startsWith('blob:')) {
        // For blob URLs, we need to determine the type from the file object
        // This will be handled by the parent component
        return 'video'; // Default to video for blob URLs
    }

    const ext = url.split('.').pop()?.toLowerCase();

    if (!ext) return 'other';

    if (['mp4', 'webm', 'ogg', 'm3u8'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'ogg'].includes(ext)) return 'audio';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    if (['pdf'].includes(ext)) return 'pdf';

    return 'other';
};

const MediaPlayer = ({
    mediaUrl,
    streamUrl = null,
    streamMimeType = null,
    fileType,
    mimeType,
    isPreview = false,
    posterImage,
    controls = true,
    isPreviewfromViewCreation = false,
    setOpenPurchaseAfterPreview = () => { },
    isNotFree = false,
    isPhysical = false,
    initialTime = null,
    onTimeUpdate = null,
    preload = 'metadata',
    onPlaybackEnded = null,
    autoPlay = false,
}) => {
    // Video.js owns its DOM. React only renders a layout-neutral root (`display: contents`) and
    // never touches what is inside it: the `[data-vjs-player]` host and the <video> element are
    // created imperatively. Previously Video.js adopted the React-rendered `[data-vjs-player]` div
    // as its player element, so any React update to that div (className change, or React reusing
    // it for the image/pdf/error branch when switching tracks) wiped the `video-js` classes and
    // left the raw control labels ("Pause", "Current Time", "Loaded: 100.00%") on the page (HYK-1139).
    const containerRef = useRef(null);
    const hostRef = useRef(null);
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const timeoutRef = useRef(null);
    const onPlaybackEndedRef = useRef(onPlaybackEnded);
    const onTimeUpdateRef = useRef(onTimeUpdate);
    const sourceTransitionSeqRef = useRef(0);
    const sourceReadyListenerRef = useRef(null);
    const sourceReadyPlayerRef = useRef(null);
    const sourceMetadataRef = useRef({
        src: null,
        type: null,
        mediaType: null,
        mimeType: null,
        fullMediaUrl: null,
    });
    // Latest preview/paywall props, read by the long-lived player listeners so they never act on
    // values captured when the player was first created (e.g. a previous track's media format).
    const previewConfigRef = useRef({
        isPreview,
        isPreviewfromViewCreation,
        isNotFree,
        isPhysical,
        setOpenPurchaseAfterPreview,
        initialTime,
    });

    useEffect(() => {
        onPlaybackEndedRef.current = onPlaybackEnded;
    }, [onPlaybackEnded]);

    useEffect(() => {
        onTimeUpdateRef.current = onTimeUpdate;
    }, [onTimeUpdate]);

    useEffect(() => {
        previewConfigRef.current = {
            isPreview,
            isPreviewfromViewCreation,
            isNotFree,
            isPhysical,
            setOpenPurchaseAfterPreview,
            initialTime,
        };
    }, [isPreview, isPreviewfromViewCreation, isNotFree, isPhysical, setOpenPurchaseAfterPreview, initialTime]);

    const clearSourceReadyListeners = () => {
        const player = sourceReadyPlayerRef.current || playerRef.current;
        const listener = sourceReadyListenerRef.current;
        if (player && listener) {
            player.off('loadedmetadata', listener);
            player.off('canplay', listener);
        }
        sourceReadyListenerRef.current = null;
        sourceReadyPlayerRef.current = null;
    };

    const isFeedCardPreview = isPreview && !isPreviewfromViewCreation;

    // Construct full URL from signed_url (backend returns relative path: /api/media/[token])
    const fullMediaUrl = getMediaUrl(mediaUrl);
    const fullStreamUrl = streamUrl ? getMediaUrl(streamUrl) : '';
    // Use fileType if provided, otherwise detect from URL
    const mediaType = fileType || getMediaType(fullMediaUrl);
    const source = resolvePlaybackSource({
        mediaUrl: fullMediaUrl,
        streamUrl: fullStreamUrl,
        mimeType,
        streamMimeType,
        mediaType: mediaType === 'audio' ? 'audio' : 'video',
    });
    const isPlayable = Boolean(fullMediaUrl) && (mediaType === 'video' || mediaType === 'audio');

    useEffect(() => {
        sourceMetadataRef.current = {
            src: source.src || fullMediaUrl,
            type: source.type || mimeType || null,
            mediaType,
            mimeType,
            fullMediaUrl,
        };
    }, [source.src, source.type, mediaType, mimeType, fullMediaUrl]);

    // Create the Video.js player while the current media is playable, and dispose it when the media
    // stops being playable (e.g. switching to an image item) or on unmount. Track switches between
    // playable sources keep the same player and only swap its source (see the effect below), so there
    // is never more than one player — and never two tracks playing at once.
    useLayoutEffect(() => {
        if (!isPlayable) return undefined;
        const container = containerRef.current;
        if (!container || !container.isConnected) return undefined;

        const host = document.createElement('div');
        host.setAttribute('data-vjs-player', '');
        const videoEl = document.createElement('video');
        videoEl.className = 'video-js vjs-default-skin w-full max-h-[500px]';
        videoEl.setAttribute('playsinline', 'true');
        if (posterImage) {
            videoEl.setAttribute('poster', posterImage);
        }
        host.appendChild(videoEl);
        container.replaceChildren(host);
        hostRef.current = host;
        videoRef.current = videoEl;

        const options = {
            controls: controls,
            autoplay: false,
            bigPlayButton: controls,
            preload: preload === 'auto' || preload === 'none' ? preload : 'metadata',
            sources: [{ src: source.src, type: source.type }],
            html5: source.isAdaptive ? { vhs: { enableLowInitialPlaylist: true } } : undefined,
            ...(isFeedCardPreview ? { inactivityTimeout: 0, muted: true } : {})
        };
        if (mediaType === 'audio') {
            options.audioPosterMode = true;
            if (posterImage) options.poster = posterImage;
        }

        let player = null;
        try {
            player = videojs(videoEl, options);
            playerRef.current = player;

            const getPreviewLimit = () => {
                const { isNotFree: paid, isPhysical: physical } = previewConfigRef.current;
                const d = player.duration();
                if (Number.isFinite(d) && d > 0) {
                    return (!paid || physical) ? d : d * 0.1;
                }
                return (!paid || physical) ? Number.MAX_SAFE_INTEGER : 3;
            };

            const enforcePreviewLimit = () => {
                if (playerRef.current !== player) return;
                const config = previewConfigRef.current;
                const shouldEnforce = config.isPreview
                    || (config.isPreviewfromViewCreation && config.isNotFree && !config.isPhysical);
                if (!shouldEnforce) return;
                const currentTime = player.currentTime();
                const limit = getPreviewLimit();
                if (currentTime >= limit) {
                    player.pause();
                    player.currentTime(0);
                    if (config.isPreviewfromViewCreation) config.setOpenPurchaseAfterPreview(true);
                }
            };

            player.on('timeupdate', () => {
                if (playerRef.current !== player) return;
                const handler = onTimeUpdateRef.current;
                if (handler && typeof handler === 'function') handler(player.currentTime());
                enforcePreviewLimit();
            });
            player.on('seeked', () => {
                if (playerRef.current !== player) return;
                enforcePreviewLimit();
            });
            player.on('ended', () => {
                const handler = onPlaybackEndedRef.current;
                if (typeof handler === 'function') handler();
            });
            player.on('error', () => {
                if (playerRef.current !== player) return;
                const mediaError = player.error?.();
                const mediaErrorCode = mediaError?.code || null;
                const mediaErrorLabel = mediaErrorCode ? `MEDIA_ERR_${mediaErrorCode}` : 'MEDIA_ERR_UNKNOWN';
                Sentry.captureException(new Error(`Video.js playback error (${mediaErrorLabel})`), {
                    tags: { component: 'MediaPlayer', action: 'playerErrorEvent' },
                    level: 'warning',
                    contexts: {
                        media: {
                            ...sourceMetadataRef.current,
                            mediaErrorCode,
                            mediaErrorMessage: mediaError?.message || null,
                            mediaErrorLabel,
                            sourceTransitionSeq: sourceTransitionSeqRef.current,
                        }
                    }
                });
            });
            // `initialTime` (continue-from-preview) is a one-shot seek for the source the player was
            // created with, not for every track the listener switches to afterwards.
            const initialSrc = source.src || fullMediaUrl;
            let initialTimeHandled = false;
            player.on('loadedmetadata', () => {
                if (playerRef.current !== player) return;
                if (initialTimeHandled) return;
                initialTimeHandled = true;
                if (sourceMetadataRef.current.src !== initialSrc) return;
                const { initialTime: startAt } = previewConfigRef.current;
                if (startAt !== null && startAt !== undefined && startAt > 0) {
                    try {
                        player.currentTime(startAt);
                    } catch (error) {
                        Sentry.captureException(error, {
                            tags: { component: 'MediaPlayer', action: 'seekToInitialTime' },
                            level: 'warning'
                        });
                    }
                }
            });
        } catch (error) {
            Sentry.captureException(error, {
                tags: { component: 'MediaPlayer', action: 'initializePlayer' },
                contexts: { media: { url: fullMediaUrl, type: mediaType, mimeType } }
            });
            playerRef.current = null;
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            clearSourceReadyListeners();
            if (player) {
                try {
                    player.pause();
                    player.dispose();
                } catch {
                    // ignore: the player may already be torn down
                }
            }
            if (playerRef.current === player) playerRef.current = null;
            // dispose() removes the host it owns; make sure nothing is left behind if it did not.
            if (host.parentNode) host.parentNode.removeChild(host);
            if (hostRef.current === host) hostRef.current = null;
            if (videoRef.current === videoEl) videoRef.current = null;
        };
        // The player is created once per playable period; later prop changes are applied by the
        // source / preview effects or read through refs, so they must not recreate the player.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlayable]);

    // Keep the host's preview classes in sync without overwriting the classes Video.js adds to it.
    useLayoutEffect(() => {
        const host = hostRef.current;
        if (!host) return;
        host.classList.toggle('preview', Boolean(isPreview));
        host.classList.toggle('feed-preview', Boolean(isFeedCardPreview));
    }, [isPlayable, isPreview, isFeedCardPreview]);

    // Update source without disposing player: stop the current track, then load the new one.
    useEffect(() => {
        if (!playerRef.current) return;
        if (!isPlayable) return;
        if (!containerRef.current?.isConnected) return;
        try {
            const player = playerRef.current;
            sourceTransitionSeqRef.current += 1;
            const sourceTransitionSeq = sourceTransitionSeqRef.current;
            clearSourceReadyListeners();
            player.pause();
            try {
                player.error?.(null);
            } catch {
                // Ignore unsupported clear-error operations.
            }
            player.src({ src: source.src, type: source.type });
            if (autoPlay) {
                let hasAttemptedAutoPlay = false;
                // Bound to both `loadedmetadata` and `canplay`: play once, and only for the latest track.
                const attemptAutoPlay = () => {
                    if (hasAttemptedAutoPlay) return;
                    if (playerRef.current !== player) return;
                    if (sourceTransitionSeq !== sourceTransitionSeqRef.current) return;
                    hasAttemptedAutoPlay = true;
                    clearSourceReadyListeners();
                    player.play()?.catch(() => { });
                };
                sourceReadyListenerRef.current = attemptAutoPlay;
                sourceReadyPlayerRef.current = player;
                player.one('loadedmetadata', attemptAutoPlay);
                player.one('canplay', attemptAutoPlay);
            }
        } catch (error) {
            Sentry.captureException(error, {
                tags: { component: 'MediaPlayer', action: 'updateVideoSource' },
                contexts: {
                    media: {
                        url: source.src || fullMediaUrl,
                        type: mediaType,
                        sourceTransitionSeq: sourceTransitionSeqRef.current,
                    }
                }
            });
        }
    }, [isPlayable, source.src, source.type, autoPlay, mediaType, fullMediaUrl]);

    // Explore/feed preview behavior: autoplay a short preview after mount/source change.
    // This is separate from `autoPlay` which is used for explicit user-initiated playback (e.g. tracklist click).
    useEffect(() => {
        const player = playerRef.current;
        if (!player) return;
        if (!isPlayable) return;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (!isPreview) return;

        if (!isPreviewfromViewCreation) {
            player.controls(false);
        }

        timeoutRef.current = setTimeout(() => {
            if (playerRef.current !== player) return;
            const shouldAutoplayMuted = isFeedCardPreview || mediaType !== 'audio';
            if (shouldAutoplayMuted) {
                player.muted(true);
            }
            player.controls(true);
            if (isFeedCardPreview) {
                player.userActive(true);
            }
            player.play().catch((error) => {
                if (error?.name !== 'NotAllowedError' && error?.name !== 'NotSupportedError') {
                    Sentry.captureException(error, {
                        tags: {
                            component: 'MediaPlayer',
                            action: 'autoplayPreview',
                            mediaType: mediaType,
                            isPreview: isPreview
                        },
                        level: 'warning',
                        contexts: {
                            media: {
                                url: fullMediaUrl,
                                type: mediaType
                            }
                        }
                    });
                }
            });
        }, 1000);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [isPlayable, isPreview, isPreviewfromViewCreation, isFeedCardPreview, mediaType, fullMediaUrl]);

    // Early return with error message if required data is missing (after all hooks have run).
    if (!fullMediaUrl || !mediaType) {
        return (
            <div key="media-error" className="p-4 ta-center">
                <p>Unable to load media. Please try again later.</p>
            </div>
        );
    }

    const handleImageLoad = () => {
        setIsLoading(false);
    };

    if (mediaType === 'image') {

        return (
            <div key="media-image" className="media-preview-container">

                {isLoading && (
                    <div className="loading-overlay">
                        <div className="spinner-border spinner-border-sm text-white" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )}
                <FullScreenImage imgUrl={fullMediaUrl} isModalOpen={isFullScreen} onClick={() => setIsFullScreen(false)} />

                <img
                    src={fullMediaUrl}
                    alt="Media content"
                    className={`img ${isPreview ? 'preview' : ''}`}
                    style={isPreview ? {
                        filter: 'blur(5px)',
                        opacity: 0.7
                    } : {}}
                    onClick={() => {
                        if (!isPreview) {
                            setIsFullScreen(true)
                        }
                    }}
                    onLoad={handleImageLoad}
                />
                {isPreview && (
                    <div className="preview-overlay">
                        {/* <span>Preview</span> */}
                    </div>
                )}
            </div>
        );
    }

    if (mediaType === 'pdf') {
        return <AlternativePdfViewer pdfUrl={fullMediaUrl} isPreview={isPreview} />;
    }

    if (mediaType === 'other') {
        return (
            <div key="media-unsupported" className="p-4 ta-center">
                <p>Unsupported file format.</p>
            </div>
        );
    }

    // React renders no children here: the Video.js host is managed imperatively (see above).
    return <div key="media-player" className="media-player-root" ref={containerRef} data-testid="media-player-root" />;
};

export default MediaPlayer;
